/* BROEK navigation: preserve each Liquid link, keep expansion separate from navigation. */
(() => {
  'use strict';
  if (window.BroekNavigationQA) return;
  const media = window.matchMedia('(min-width: 1025px)');
  const initialized = new WeakSet();
  const records = [];
  const direct = (parent, selector) => Array.from(parent?.children || []).find(el => el.matches(selector));
  const text = value => String(value || '').replace(/\[(hot|new|sale|popular|coming soon)\]/gi, '').trim();
  const panelFor = row => {
    const next = row?.nextElementSibling;
    return next?.matches('.submenu,.sub-children-menu') ? next : null;
  };
  const controls = row => Array.from(row?.querySelectorAll('open-children-toggle') || []).filter(el => el.closest('menu-item') === row);
  function sync(record) {
    record.rows.forEach(row => {
      const panel = panelFor(row);
      const expanded = media.matches ? row.parentElement.classList.contains('visible') : row.classList.contains('is-open');
      controls(row).forEach(control => control.setAttribute('aria-expanded', String(expanded)));
      // Desktop visibility remains governed by the theme's hover/focus styling.
      panel.inert = !media.matches && !expanded;
    });
  }
  function closeBranch(record, row) {
    row?.classList.remove('is-open');
    row?.parentElement.classList.remove('visible');
    const panel = panelFor(row);
    panel?.querySelectorAll('menu-item.is-open').forEach(child => child.classList.remove('is-open'));
    panel?.querySelectorAll('.visible').forEach(child => child.classList.remove('visible'));
    sync(record);
  }
  function reset(record) {
    record.rows.forEach(row => { row.classList.remove('is-open'); row.parentElement.classList.remove('visible'); });
    sync(record);
  }
  function expand(record, row, focusPanel = false) {
    const panel = panelFor(row);
    if (!panel) return;
    const isOpen = media.matches ? row.parentElement.classList.contains('visible') : row.classList.contains('is-open');
    const parentList = row.parentElement.parentElement;
    Array.from(parentList.children).forEach(item => {
      const sibling = direct(item, 'menu-item');
      if (sibling && sibling !== row) closeBranch(record, sibling);
    });
    row.classList.toggle('is-open', !isOpen);
    row.parentElement.classList.toggle('visible', !isOpen);
    sync(record);
    if (!isOpen) {
      panel.scrollTop = 0;
      if (focusPanel) panel.querySelector(media.matches ? 'a[href]' : 'back-menu,a[href]')?.focus({preventScroll:true});
    }
  }
  function bindRoutes(record, list, items) {
    if (!list || !Array.isArray(items)) return;
    const children = Array.from(list.children).filter(el => el.tagName === 'LI');
    items.forEach((item, index) => {
      const element = children[index];
      const row = direct(element, 'menu-item');
      const anchor = direct(row, 'a') || direct(element, 'a');
      const label = direct(anchor, 'span')?.textContent || anchor?.textContent;
      // Match within this branch, never by a global title such as "T-Shirts".
      if (!anchor || text(label) !== text(item.title) || !item.url) return;
      anchor.dataset.broekMenuDestination = item.url;
      anchor.setAttribute('href', item.url);
      if (item.items?.length) {
        const panel = panelFor(row);
        const childList = panel?.classList.contains('sub-children-menu') ? direct(panel, 'ul') : panel?.querySelector('.subchildmenu');
        bindRoutes(record, childList, item.items);
      }
    });
  }
  function init() {
    document.querySelectorAll('script[data-broek-navigation-source]').forEach(source => {
      if (initialized.has(source)) return;
      let items;
      try { items = JSON.parse(source.textContent); } catch (_) { return; }
      const header = source.closest('header');
      if (!header || !Array.isArray(items)) return;
      initialized.add(source);
      header.querySelectorAll('nav.navigation.horizontal').forEach((nav, navIndex) => {
        if (nav.hasAttribute('data-broek-navigation')) return;
        nav.dataset.broekNavigation = nav.classList.contains('mobile') ? 'mobile' : 'desktop';
        const list = nav.querySelector('.navigation__menu-content > .horizontal-list');
        const rows = Array.from(nav.querySelectorAll('menu-item')).filter(row => panelFor(row));
        const record = {nav, rows};
        records.push(record);
        bindRoutes(record, list, items);
        rows.forEach((row, index) => {
          const panel = panelFor(row);
          if (!panel.id) panel.id = `broek-nav-${source.dataset.broekNavigationSource}-${navIndex}-${index}`;
          const label = direct(direct(row, 'a'), 'span')?.textContent?.trim() || 'menu';
          controls(row).forEach(control => {
            control.setAttribute('role', 'button');
            control.setAttribute('tabindex', '0');
            control.setAttribute('aria-label', `Open ${label} submenu`);
            control.setAttribute('aria-controls', panel.id);
          });
        });
        nav.querySelectorAll('back-menu,close-menu').forEach(control => {
          control.setAttribute('role', 'button');
          control.setAttribute('tabindex', '0');
          control.setAttribute('aria-label', control.matches('back-menu') ? 'Back to previous menu' : 'Close menu');
        });
        nav.addEventListener('click', event => {
          if (!(event.target instanceof Element)) return;
          const toggle = event.target.closest('open-children-toggle');
          if (toggle && nav.contains(toggle)) {
            const row = toggle.closest('menu-item');
            if (!panelFor(row)) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            expand(record, row, event.detail === 0);
            return;
          }
          const back = event.target.closest('back-menu');
          if (back && nav.contains(back)) {
            const panel = back.closest('.submenu,.sub-children-menu');
            const row = panel?.previousElementSibling;
            if (!row?.matches('menu-item')) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            closeBranch(record, row);
            const control = controls(row).find(el => el.getClientRects().length) || direct(row, 'a');
            control?.focus({preventScroll:true});
            return;
          }
          if (event.target.closest('close-menu')) {
            // Keep the native drawer close/scroll cleanup in charge.
            reset(record);
            return;
          }
          const anchor = event.target.closest('a[data-broek-menu-destination]');
          if (!anchor || !nav.contains(anchor)) return;
          anchor.setAttribute('href', anchor.dataset.broekMenuDestination);
          // Do not cancel the browser's own navigation, including Ctrl/Cmd/new-tab clicks.
          // Stop parent menu handlers from substituting another branch's first link.
          event.stopPropagation();
        }, true);
        nav.addEventListener('keydown', event => {
          if (!(event.target instanceof Element)) return;
          const control = event.target.closest('open-children-toggle,back-menu,close-menu');
          if (control && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault(); event.stopImmediatePropagation(); control.click(); return;
          }
          if (event.key === 'Escape') {
            const panel = event.target.closest('.submenu,.sub-children-menu');
            const row = panel?.previousElementSibling;
            if (row?.matches('menu-item')) {
              event.preventDefault(); event.stopImmediatePropagation(); closeBranch(record, row);
              direct(row, 'a')?.focus({preventScroll:true});
            }
          }
        }, true);
        sync(record);
      });
    });
  }
  media.addEventListener('change', () => records.filter(record => record.nav.isConnected).forEach(reset));
  document.addEventListener('shopify:section:load', init);
  window.addEventListener('pageshow', event => {
    if (event.persisted) records.filter(record => record.nav.isConnected).forEach(reset);
  });
  window.BroekNavigationQA = {version:'06sep-r1'};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true});
  else init();
})();
